// Triggers on the board (qa-runs#1426): a cue (`cues.ts`) on a painted area may also STOP MOVEMENT. A move whose route
// walks into such an area is cut by the server on the first square of it, before the move is committed (a long step or
// a fast drag included: the route is walked a square at a time), and the token that set it off — or every token on the
// scene — is held where it stands until the DM releases it or a fight starts.
//
// The hold is the scene's `paused`: the token ids held, never a reason. It is separate from `locked` and from the turn
// lock (design/11 §7, design/20): the DM always moves a held token, a player is refused with "Movement paused by the
// DM". A player is sent only the held ids of their own tokens they see; the trigger, its area and how many there are
// reach no player, so nothing changes for them before it fires.
import { isGridless } from '../grid/grid.js';
import { stepsBetween } from '../grid/measure.js';
import { stepCell } from '../grid/cells.js';
import { footprintSquares } from './fog.js';
import { spanTest } from './zones.js';
export const MOVEMENT_PAUSED = 'Movement paused by the DM.';
/** The most tokens one hold names: every token on a full scene. */
export const PAUSE_LIMITS = { ids: 2000 };
/** The most triggers one scene keeps waiting at once: every trigger a table holds. */
export const WAIT_LIMIT = 250;
export const PAUSE_KINDS = ['move.pause', 'move.release', 'trigger.answer'];
export const isPauseBody = (b) => PAUSE_KINDS.includes(b.kind);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isIds = (v) => Array.isArray(v) && v.length >= 1 && v.length <= PAUSE_LIMITS.ids && v.every(isUuid);
const isWait = (v) => typeof v === 'object' && v !== null && Object.keys(v).length === 2 && isUuid(v.cue) && isUuid(v.token);
export function parsePauseBody(b) {
    if (b.kind === 'move.pause') {
        if (Object.keys(b).some((k) => !['kind', 'ids', 'all', 'cues', 'waits'].includes(k)))
            return null;
        if (b.ids !== undefined && !isIds(b.ids))
            return null;
        if (b.all !== undefined && b.all !== true)
            return null;
        if (b.cues !== undefined && !isIds(b.cues))
            return null;
        if (b.waits !== undefined && !(Array.isArray(b.waits) && b.waits.length >= 1 && b.waits.length <= WAIT_LIMIT && b.waits.every(isWait)))
            return null;
        if (!b.ids && !b.all)
            return null;
        return { kind: 'move.pause', ...(b.ids ? { ids: [...b.ids] } : {}), ...(b.all ? { all: true } : {}), ...(b.cues ? { cues: [...b.cues] } : {}), ...(b.waits ? { waits: b.waits.map((w) => ({ cue: w.cue, token: w.token })) } : {}) };
    }
    if (b.kind === 'trigger.answer') {
        if (Object.keys(b).some((k) => !['kind', 'cue', 'token', 'fire'].includes(k)))
            return null;
        if (!isUuid(b.cue) || !isUuid(b.token) || typeof b.fire !== 'boolean')
            return null;
        return { kind: 'trigger.answer', cue: b.cue, token: b.token, fire: b.fire };
    }
    if (b.kind === 'move.release') {
        if (Object.keys(b).some((k) => !['kind', 'ids'].includes(k)))
            return null;
        if (b.ids !== undefined && !isIds(b.ids))
            return null;
        return { kind: 'move.release', ...(b.ids ? { ids: [...b.ids] } : {}) };
    }
    return null;
}
/** The held ids after a pause or a release; `onScene` the ids of what stands on the scene now (`all` holds each). Empty: none. */
export function reducePause(paused, body, onScene) {
    const cur = new Set(paused ?? []);
    if (body.kind === 'trigger.answer') {
        if (!body.fire)
            cur.delete(body.token);
        return [...cur];
    }
    if (body.kind === 'move.release') {
        if (!body.ids)
            return [];
        for (const id of body.ids)
            cur.delete(id);
        return [...cur];
    }
    for (const id of body.all ? onScene : body.ids ?? [])
        if (onScene.includes(id))
            cur.add(id);
    return [...cur].slice(0, PAUSE_LIMITS.ids);
}
/** The waits after a pause or an answer: a pause's join the end in order (none twice); an answer takes the first matching away. */
export function reduceWaits(waiting, body) {
    const cur = (waiting ?? []).map((w) => ({ ...w }));
    if (body.kind === 'trigger.answer') {
        const i = cur.findIndex((w) => w.cue === body.cue && w.token === body.token);
        if (i >= 0)
            cur.splice(i, 1);
        return cur;
    }
    if (body.kind !== 'move.pause' || !body.waits)
        return cur;
    for (const w of body.waits)
        if (!cur.some((x) => x.cue === w.cue && x.token === w.token))
            cur.push({ ...w });
    return cur.slice(0, WAIT_LIMIT);
}
export const TRIGGER_NOT_WAITING = 'That trigger is no longer waiting.';
/** A trigger that asks the DM first: one that springs a trap or starts a fight, unless switched to fire on its own. */
export const asksFirst = (c) => !!(c.fight || c.trap) && !c.auto;
/** Whether a token may set this trigger off: any token, only a player's (one with owners), or the one it names. */
export function triggerWho(c, token) {
    if (!c.who)
        return true;
    return c.who === 'players' ? token.owners.length > 0 : c.who === token.id;
}
/**
 * The triggers that stop movement and may fire on a move: on, on an area, and stopping — a trap or a fight stops the token
 * that set it off too, so the DM is asked while it stands there. `dmHere` false: those that ask first are left out, so
 * with no DM at the table one neither fires nor stops anybody.
 */
export const stoppingTriggers = (cues, dmHere = true) => cues.filter((c) => !c.off && c.trigger.on === 'area_enter' && (c.stop || c.fight || c.trap) && (dmHere || !asksFirst(c)));
/**
 * Where a move is cut by the triggers that stop movement (qa-runs#1426): walking the route a square at a time from where
 * the token stands, the first square of its footprint that ENTERS one of the triggers' areas ends the move there; every
 * trigger entered on that same square fires. Null: none is entered (or the map has no grid). The route returned ends on
 * that square: the route's own point when it is one, else the square's centre for the token's size.
 */
export function triggerCut(triggers, zones, grid, extent, token, route) {
    if (isGridless(grid) || !route.length)
        return null;
    const live = triggers.filter((c) => c.trigger.on === 'area_enter' && triggerWho(c, token));
    if (!live.length)
        return null;
    const tests = new Map();
    for (const z of zones)
        if (live.some((c) => c.trigger.zoneId === z.id))
            tests.set(z.id, spanTest(z.spans));
    if (!tests.size)
        return null;
    const inside = (b, test) => { for (let j = b.j0; j <= b.j1; j += 1)
        for (let i = b.i0; i <= b.i1; i += 1)
            if (test({ i, j }))
                return true; return false; };
    const box = (p) => footprintSquares(grid, extent, p, token.size);
    let prevPoint = { x: token.x, y: token.y };
    let prev = box(prevPoint);
    const was = new Map([...tests].map(([id, t]) => [id, inside(prev, t)]));
    for (let k = 0; k < route.length; k += 1) {
        const p = route[k];
        const b = box(p);
        const w = b.i1 - b.i0;
        const h = b.j1 - b.j0;
        for (const c of stepsBetween({ i: prev.i0, j: prev.j0 }, { i: b.i0, j: b.j0 })) {
            const step = { i0: c.i, j0: c.j, i1: c.i + w, j1: c.j + h };
            const entered = new Set();
            for (const [id, t] of tests) {
                const now = inside(step, t);
                if (now && !was.get(id))
                    entered.add(id);
                was.set(id, now);
            }
            if (!entered.size)
                continue;
            const fired = live.filter((x) => entered.has(x.trigger.zoneId));
            const last = c.i === b.i0 && c.j === b.j0 ? p : { ...p, ...stepCell(grid, extent, prevPoint, token.size, c.i - prev.i0, c.j - prev.j0) };
            return { route: [...route.slice(0, k), last], cues: fired, all: fired.some((x) => x.stop === 'all') };
        }
        prev = b;
        prevPoint = p;
    }
    return null;
}
/**
 * A group move's cut (the DM's `objects.move`): each mover's route is cut on the first square of a trigger's area as one
 * move's is, and every trigger entered fires once for the whole group, set off by the first mover to enter it.
 */
export function groupCut(triggers, zones, grid, extent, movers) {
    const routes = new Map();
    const cues = [];
    let all = false;
    for (const m of movers) {
        const cut = triggerCut(triggers, zones, grid, extent, m.token, m.route);
        if (!cut)
            continue;
        routes.set(m.token.id, cut.route);
        all ||= cut.all;
        for (const c of cut.cues)
            if (!cues.some((x) => x.cue.id === c.id))
                cues.push({ cue: c, token: m.token.id });
    }
    return routes.size ? { routes, cues, all } : null;
}
/**
 * A fight a trigger starts (issue's Decided 5): the tokens the DM picked that still stand on the scene, and every player
 * token there, each not yet in the fight joins it as a combatant; the picked ones hidden are revealed as it starts.
 */
export function fightPlan(objects, inFight, picks) {
    const picked = picks.map((id) => objects[id]).filter((o) => !!o);
    const players = Object.values(objects).filter((o) => o.owners.length > 0 && !picks.includes(o.id));
    return {
        reveal: picked.filter((o) => o.hidden).map((o) => o.id),
        join: [...picked, ...players].filter((o) => !inFight(o.id)).map((o) => ({ id: o.id, name: o.name, initBonus: o.values.initBonus ?? 0 })),
    };
}
/** A player's hold (qa-runs#1426): the held ids of tokens they own and see. Nothing of anyone else's. */
export function playerPaused(paused, mine) {
    return (paused ?? []).filter(mine);
}
//# sourceMappingURL=triggers.js.map